import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '@/pages/Dashboard';
import { getNavigationEntryDetail } from '@/services/api/navigation-report';
import type { NavRequestFilters } from '@/types/navigation-report';
import { DetailCards } from './DetailCards';
import { HourlyChart } from './HourlyChart';
import { NavigationWeb } from './NavigationWeb';
import { NextPagesTable } from './NextPagesTable';
import { UtmsTable } from './UtmsTable';
import { fmtNav } from './format';

interface EntryDetailProps {
    entryPath: string;
    request: NavRequestFilters;
    periodLabel: string;
    onBack: () => void;
    onOpenAsEntry: (path: string) => void;
}

const DETAIL_LIMIT = 500;

/** Estado 2 da tela: tudo aqui é exclusivo da página de entrada aberta. */
export function EntryDetail({ entryPath, request, periodLabel, onBack, onOpenAsEntry }: EntryDetailProps) {
    const params = { ...request, limit: DETAIL_LIMIT };
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['navigation_entry_detail', entryPath, params],
        queryFn: () => getNavigationEntryDetail(entryPath, params),
        retry: (count, err: any) => count < 1 && ![403, 422].includes(err?.response?.status),
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                <button type="button" onClick={onBack} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5" /> Voltar às entradas
                </button>
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">página de entrada</p>
                    <p className="truncate text-sm font-semibold text-foreground" title={entryPath}>{entryPath}</p>
                </div>
                {data && (
                    <p className="text-[10px] text-muted-foreground">
                        {data.meta.domains.length} domínio(s) · {data.meta.start} → {data.meta.end}
                    </p>
                )}
            </div>

            {isError ? (
                <ErrorState message={(error as any)?.response?.data?.detail || (error as Error)?.message} onRetry={() => refetch()} />
            ) : isLoading || !data ? (
                <div className="flex items-center justify-center rounded-lg border border-border bg-card py-16 text-muted-foreground">
                    <div className="mr-3 h-4 w-4 animate-spin rounded-full border-b-2 border-muted-foreground" />
                    <span className="text-sm">Montando a teia desta entrada…</span>
                </div>
            ) : (
                <>
                    <DetailCards detail={data.detail} periodLabel={periodLabel} />

                    <div className="grid gap-4 xl:grid-cols-5">
                        <div className="xl:col-span-3">
                            <NavigationWeb detail={data.detail} onOpenAsEntry={onOpenAsEntry} />
                        </div>
                        <div className="xl:col-span-2">
                            <NextPagesTable items={data.detail.next_pages} onOpenAsEntry={onOpenAsEntry} />
                        </div>
                    </div>

                    <UtmsTable items={data.detail.utms} />

                    <HourlyChart hourly={data.detail.hourly} totalSessions={data.detail.sessions} />

                    <p className="text-right text-[10px] text-muted-foreground">
                        {fmtNav.num(data.detail.sessions)} sessões entraram por esta página · sessão não cruza domínio · bots excluídos
                    </p>
                </>
            )}
        </div>
    );
}
